import {
    chartRefreshScheduler,
    computeStaleState,
    formatAge,
    FrameBudgetTracker,
    measureFrame,
    RingBuffer,
    getStaleThresholdMs,
  getOptionsLiquidityPolicy,
    setSnapshot,
    getSnapshot,
    reportChartRenderMetric,
} from '../../charts/engine';
import { renderDonutChart, renderHeatmapGrid, renderLineChart } from '../../charts/primitives';
import { apiFetch } from '../../services/api-client';
import { isFeatureEnabled } from '../../services/runtime-config';

type UpstoxStrike = {
    strike: number;
    ceOI: number;
    ceChange: number;
  ceVolume?: number;
    peOI: number;
    peChange: number;
  peVolume?: number;
    ceBidAsk?: { bid: number; ask: number } | null;
    peBidAsk?: { bid: number; ask: number } | null;
};

type SymbolEventDetail = { symbol?: string };

type UpstoxOptionsPayload = {
    symbol: string;
    expiry: string;
    spotPrice: number;
    pcr: number;
    totalCeOI: number;
    totalPeOI: number;
    strikes: UpstoxStrike[];
};

const teardownMap = new WeakMap<HTMLElement, () => void>();

type OptionsPersistedSnapshot = {
  symbol: string;
  primaryExpiry: string | null;
  compareExpiry: string | null;
  payload: UpstoxOptionsPayload;
  comparePayload: UpstoxOptionsPayload | null;
  pcrSeries: number[];
  oiSkewSeries: number[];
  flowImbalanceSeries: number[];
  atmVolumePulseSeries: number[];
  oiConcentrationSeries: number[];
  clusterTrends: {
    put: number[];
    atm: number[];
    call: number[];
  };
  lastUpdatedAt: number;
};

type ClusterId = 'put' | 'atm' | 'call';
type ClusterLevel = 'normal' | 'elevated' | 'critical';

type ClusterAlertState = {
  id: ClusterId;
  label: string;
  level: ClusterLevel;
  enteredAt: number;
  score: number;
  pendingLevel: ClusterLevel | null;
  pendingCount: number;
};

function toIsoDate(value: Date): string {
  return value.toISOString().split('T')[0];
}

function buildWeeklyExpiries(fromIso: string, count: number = 5): string[] {
  const base = new Date(fromIso);
  if (Number.isNaN(base.getTime())) return [];
  const out: string[] = [];
  for (let i = 0; i < count; i += 1) {
    const next = new Date(base);
    next.setDate(base.getDate() + (i * 7));
    out.push(toIsoDate(next));
  }
  return out;
}

function levelRank(level: ClusterLevel): number {
  if (level === 'critical') return 3;
  if (level === 'elevated') return 2;
  return 1;
}

function formatDwell(ms: number): string {
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60000) return `${Math.floor(ms / 1000)}s`;
  return `${Math.floor(ms / 60000)}m`;
}

export function renderPhase2OptionsMicrostructure(container: HTMLElement, initialSymbol: string = 'NIFTY'): void {
    if (!isFeatureEnabled('charts_v2_engine')) {
        container.innerHTML = '<div class="wm-empty">charts_v2_engine flag is disabled.</div>';
        return;
    }

    teardownMap.get(container)?.();

    container.innerHTML = `
      <div class="v2-chart-card glass-card">
        <div class="card-header">
          <h3>Phase 2 Engine: Options Microstructure</h3>
          <div class="v2-options-controls v2-options-controls--compact">
            <button class="glass-btn glass-btn--sm v2-opt-btn" data-opt-symbol="NIFTY">NIFTY</button>
            <button class="glass-btn glass-btn--sm v2-opt-btn" data-opt-symbol="BANKNIFTY">BANKNIFTY</button>
            <button class="glass-btn glass-btn--sm v2-opt-btn" data-opt-symbol="FINNIFTY">FINNIFTY</button>
          </div>
        </div>

        <div class="v2-options-expiry-row">
          <label class="v2-options-expiry-label">
            Active Expiry
            <select id="v2-opt-expiry" class="v2-options-expiry-select"></select>
          </label>
          <label class="v2-options-expiry-label">
            Compare Expiry
            <select id="v2-opt-compare-expiry" class="v2-options-expiry-select"></select>
          </label>
        </div>

        <div class="v2-chart-grid">
          <div class="v2-chart-slot">
            <div class="v2-chart-title">OI Imbalance by Strike</div>
            <canvas id="v2-opt-line" class="v2-chart-canvas"></canvas>
          </div>

          <div class="v2-chart-slot">
            <div class="v2-chart-title">Call/Put OI Split</div>
            <canvas id="v2-opt-donut" class="v2-chart-canvas"></canvas>
          </div>

          <div class="v2-chart-slot v2-chart-slot--wide">
            <div class="v2-chart-title">Bid/Ask Spread Heatmap</div>
            <canvas id="v2-opt-heatmap" class="v2-chart-canvas"></canvas>
          </div>
        </div>

        <div class="v2-chart-meta">
          <span id="v2-opt-freshness" class="text-xs">Waiting for live option-chain feed...</span>
          <span id="v2-opt-frame" class="text-xs text-muted" style="margin-left: var(--space-3);">Frame: --</span>
          <span id="v2-opt-context" class="text-xs text-muted" style="margin-left: var(--space-3);"></span>
        </div>

        <div id="v2-opt-liquidity-warnings" class="v2-liquidity-warnings"></div>
        <div id="v2-opt-liquidity-overlay" class="v2-liquidity-overlay"></div>

        <div class="v2-options-diagnostics-grid">
          <div class="v2-options-diagnostics-slot">
            <div class="v2-chart-title">OI Shift Ladder (ATM Window)</div>
            <div id="v2-opt-oi-ladder" class="v2-options-ladder"></div>
          </div>
          <div class="v2-options-diagnostics-slot">
            <div class="v2-chart-title">Unusual Flow Candidates</div>
            <div id="v2-opt-unusual-flow" class="v2-options-flow"></div>
          </div>
        </div>

        <div class="v2-options-depth-strip-board">
          <div class="v2-chart-title">Strike Depth Imbalance Strip (Active Expiry)</div>
          <div id="v2-opt-depth-strip" class="v2-options-depth-strip"></div>
        </div>

        <div class="v2-options-cluster-board">
          <div class="v2-chart-title">Spread-Depth Stress Clusters (Active Expiry)</div>
          <div id="v2-opt-stress-clusters" class="v2-options-cluster"></div>
        </div>

        <div class="v2-options-cluster-trends">
          <div class="v2-options-cluster-trend-slot">
            <div class="v2-chart-title">Put Wing Stress Trend</div>
            <canvas id="v2-opt-cluster-put-trend" class="v2-chart-canvas"></canvas>
          </div>
          <div class="v2-options-cluster-trend-slot">
            <div class="v2-chart-title">ATM Band Stress Trend</div>
            <canvas id="v2-opt-cluster-atm-trend" class="v2-chart-canvas"></canvas>
          </div>
          <div class="v2-options-cluster-trend-slot">
            <div class="v2-chart-title">Call Wing Stress Trend</div>
            <canvas id="v2-opt-cluster-call-trend" class="v2-chart-canvas"></canvas>
          </div>
        </div>

        <div class="v2-options-cluster-trends">
          <div class="v2-options-cluster-trend-slot">
            <div class="v2-chart-title">PCR Regime Pulse</div>
            <canvas id="v2-opt-pcr-pulse" class="v2-chart-canvas"></canvas>
          </div>
          <div class="v2-options-cluster-trend-slot">
            <div class="v2-chart-title">OI Skew Pulse</div>
            <canvas id="v2-opt-oi-skew-pulse" class="v2-chart-canvas"></canvas>
          </div>
          <div class="v2-options-cluster-trend-slot">
            <div class="v2-chart-title">Flow Concentration</div>
            <div id="v2-opt-flow-concentration" class="v2-sector-board"></div>
          </div>
        </div>

        <div class="v2-options-cluster-trends">
          <div class="v2-options-cluster-trend-slot">
            <div class="v2-chart-title">ATM Net OI Pressure</div>
            <div id="v2-opt-atm-pressure" class="v2-sector-board"></div>
          </div>
          <div class="v2-options-cluster-trend-slot">
            <div class="v2-chart-title">CE/PE Flow Imbalance Pulse</div>
            <canvas id="v2-opt-flow-imbalance-pulse" class="v2-chart-canvas"></canvas>
          </div>
          <div class="v2-options-cluster-trend-slot">
            <div class="v2-chart-title">ATM Volume Pulse</div>
            <canvas id="v2-opt-atm-volume-pulse" class="v2-chart-canvas"></canvas>
          </div>
          <div class="v2-options-cluster-trend-slot">
            <div class="v2-chart-title">OI Concentration Pulse</div>
            <canvas id="v2-opt-oi-concentration-pulse" class="v2-chart-canvas"></canvas>
          </div>
        </div>

        <div class="v2-options-compare-board">
          <div class="v2-chart-title">Cross Expiry OI Comparison</div>
          <div id="v2-opt-expiry-compare" class="v2-options-compare"></div>
        </div>

        <div id="v2-opt-stale" class="v2-stale-overlay" style="display:none;">
          Option chain feed delayed. Rendering cached snapshot.
        </div>
      </div>
    `;

    const lineCanvas = container.querySelector('#v2-opt-line') as HTMLCanvasElement | null;
    const donutCanvas = container.querySelector('#v2-opt-donut') as HTMLCanvasElement | null;
    const heatmapCanvas = container.querySelector('#v2-opt-heatmap') as HTMLCanvasElement | null;
    const putTrendCanvas = container.querySelector('#v2-opt-cluster-put-trend') as HTMLCanvasElement | null;
    const atmTrendCanvas = container.querySelector('#v2-opt-cluster-atm-trend') as HTMLCanvasElement | null;
    const callTrendCanvas = container.querySelector('#v2-opt-cluster-call-trend') as HTMLCanvasElement | null;
    const pcrPulseCanvas = container.querySelector('#v2-opt-pcr-pulse') as HTMLCanvasElement | null;
    const oiSkewPulseCanvas = container.querySelector('#v2-opt-oi-skew-pulse') as HTMLCanvasElement | null;
    const flowImbalancePulseCanvas = container.querySelector('#v2-opt-flow-imbalance-pulse') as HTMLCanvasElement | null;
    const atmVolumePulseCanvas = container.querySelector('#v2-opt-atm-volume-pulse') as HTMLCanvasElement | null;
    const oiConcentrationPulseCanvas = container.querySelector('#v2-opt-oi-concentration-pulse') as HTMLCanvasElement | null;
    const freshnessNode = container.querySelector('#v2-opt-freshness') as HTMLElement | null;
    const frameNode = container.querySelector('#v2-opt-frame') as HTMLElement | null;
    const contextNode = container.querySelector('#v2-opt-context') as HTMLElement | null;
    const expirySelect = container.querySelector('#v2-opt-expiry') as HTMLSelectElement | null;
    const compareExpirySelect = container.querySelector('#v2-opt-compare-expiry') as HTMLSelectElement | null;
    const warningsNode = container.querySelector('#v2-opt-liquidity-warnings') as HTMLElement | null;
    const overlayNode = container.querySelector('#v2-opt-liquidity-overlay') as HTMLElement | null;
    const ladderNode = container.querySelector('#v2-opt-oi-ladder') as HTMLElement | null;
    const unusualFlowNode = container.querySelector('#v2-opt-unusual-flow') as HTMLElement | null;
    const depthStripNode = container.querySelector('#v2-opt-depth-strip') as HTMLElement | null;
    const clusterNode = container.querySelector('#v2-opt-stress-clusters') as HTMLElement | null;
    const flowConcentrationNode = container.querySelector('#v2-opt-flow-concentration') as HTMLElement | null;
    const atmPressureNode = container.querySelector('#v2-opt-atm-pressure') as HTMLElement | null;
    const compareNode = container.querySelector('#v2-opt-expiry-compare') as HTMLElement | null;
    const staleNode = container.querySelector('#v2-opt-stale') as HTMLElement | null;

    if (!lineCanvas || !donutCanvas || !heatmapCanvas || !putTrendCanvas || !atmTrendCanvas || !callTrendCanvas || !pcrPulseCanvas || !oiSkewPulseCanvas || !flowImbalancePulseCanvas || !atmVolumePulseCanvas || !oiConcentrationPulseCanvas || !freshnessNode || !frameNode || !contextNode || !expirySelect || !compareExpirySelect || !warningsNode || !overlayNode || !ladderNode || !unusualFlowNode || !depthStripNode || !clusterNode || !flowConcentrationNode || !atmPressureNode || !compareNode || !staleNode) return;

    let symbol = initialSymbol.toUpperCase();
    let primaryExpiry: string | null = null;
    let compareExpiry: string | null = null;
    let compareSnapshot: UpstoxOptionsPayload | null = null;
    let lastUpdatedAt: number | null = null;
    let lastSnapshot: UpstoxOptionsPayload | null = null;
    const frameTracker = new FrameBudgetTracker(200, 16);
    const putClusterTrend = new RingBuffer<number>(180);
    const atmClusterTrend = new RingBuffer<number>(180);
    const callClusterTrend = new RingBuffer<number>(180);
    const pcrSeries = new RingBuffer<number>(180);
    const oiSkewSeries = new RingBuffer<number>(180);
    const flowImbalanceSeries = new RingBuffer<number>(180);
    const atmVolumePulseSeries = new RingBuffer<number>(180);
    const oiConcentrationSeries = new RingBuffer<number>(180);
    const staleThresholdMs = getStaleThresholdMs('B');
    const snapshotKey = 'phase2:options-microstructure';
    const clusterAlerts: Record<ClusterId, ClusterAlertState> = {
      put: { id: 'put', label: 'PUT WING', level: 'normal', enteredAt: Date.now(), score: 0, pendingLevel: null, pendingCount: 0 },
      atm: { id: 'atm', label: 'ATM BAND', level: 'normal', enteredAt: Date.now(), score: 0, pendingLevel: null, pendingCount: 0 },
      call: { id: 'call', label: 'CALL WING', level: 'normal', enteredAt: Date.now(), score: 0, pendingLevel: null, pendingCount: 0 },
    };

    const persisted = getSnapshot<OptionsPersistedSnapshot>(snapshotKey);
    if (persisted?.payload) {
      symbol = persisted.symbol.toUpperCase();
      primaryExpiry = persisted.primaryExpiry;
      compareExpiry = persisted.compareExpiry;
      lastSnapshot = persisted.payload;
      compareSnapshot = persisted.comparePayload;
      persisted.pcrSeries?.forEach((value) => pcrSeries.push(value));
      persisted.oiSkewSeries?.forEach((value) => oiSkewSeries.push(value));
      persisted.flowImbalanceSeries?.forEach((value) => flowImbalanceSeries.push(value));
      persisted.atmVolumePulseSeries?.forEach((value) => atmVolumePulseSeries.push(value));
      persisted.oiConcentrationSeries?.forEach((value) => oiConcentrationSeries.push(value));
      persisted.clusterTrends?.put?.forEach((value) => putClusterTrend.push(value));
      persisted.clusterTrends?.atm?.forEach((value) => atmClusterTrend.push(value));
      persisted.clusterTrends?.call?.forEach((value) => callClusterTrend.push(value));
      lastUpdatedAt = persisted.lastUpdatedAt;
    }

    const syncExpiryControls = (activeExpiry: string): void => {
      const options = buildWeeklyExpiries(activeExpiry, 6);
      if (!options.includes(activeExpiry)) {
        options.unshift(activeExpiry);
      }

      if (!compareExpiry || compareExpiry === activeExpiry) {
        compareExpiry = options.find((value) => value !== activeExpiry) || activeExpiry;
      }

      expirySelect.innerHTML = options.map((value) => (
        `<option value="${value}">${value}</option>`
      )).join('');

      compareExpirySelect.innerHTML = options.map((value) => (
        `<option value="${value}">${value}</option>`
      )).join('');

      expirySelect.value = activeExpiry;
      if (!options.includes(compareExpiry)) {
        compareExpiry = options.find((value) => value !== activeExpiry) || activeExpiry;
      }
      compareExpirySelect.value = compareExpiry;
    };

    const buildCrossExpiryRows = (primary: UpstoxOptionsPayload, secondary: UpstoxOptionsPayload | null): string => {
      if (!secondary) {
        return '<div class="v2-options-compare__empty">Comparison expiry unavailable for current symbol window.</div>';
      }

      const pcrDelta = primary.pcr - secondary.pcr;
      const ceOiDelta = primary.totalCeOI - secondary.totalCeOI;
      const peOiDelta = primary.totalPeOI - secondary.totalPeOI;
      const netOiDelta = (primary.totalPeOI - primary.totalCeOI) - (secondary.totalPeOI - secondary.totalCeOI);
      const atmPrimary = primary.strikes.reduce((best, item) => {
        if (!best) return item;
        const a = Math.abs(item.strike - primary.spotPrice);
        const b = Math.abs(best.strike - primary.spotPrice);
        return a < b ? item : best;
      }, primary.strikes[0]);
      const atmSecondary = secondary.strikes.reduce((best, item) => {
        if (!best) return item;
        const a = Math.abs(item.strike - primary.spotPrice);
        const b = Math.abs(best.strike - primary.spotPrice);
        return a < b ? item : best;
      }, secondary.strikes[0]);

      const atmNetPrimary = (atmPrimary?.peOI || 0) - (atmPrimary?.ceOI || 0);
      const atmNetSecondary = (atmSecondary?.peOI || 0) - (atmSecondary?.ceOI || 0);
      const atmNetDelta = atmNetPrimary - atmNetSecondary;

      const secondaryByStrike = new Map<number, UpstoxStrike>();
      secondary.strikes.forEach((item) => {
        secondaryByStrike.set(item.strike, item);
      });

      const strikeShiftRows = primary.strikes.map((item) => {
        const against = secondaryByStrike.get(item.strike);
        const primaryNet = item.peOI - item.ceOI;
        const secondaryNet = against ? (against.peOI - against.ceOI) : 0;
        return {
          strike: item.strike,
          delta: primaryNet - secondaryNet,
        };
      }).sort((a, b) => Math.abs(b.delta) - Math.abs(a.delta)).slice(0, 4);

      return `
        <div class="v2-options-compare__headline">${primary.expiry} vs ${secondary.expiry}</div>
        <div class="v2-options-compare__stats">
          <span class="v2-liquidity-chip">PCR Δ ${pcrDelta >= 0 ? '+' : ''}${pcrDelta.toFixed(2)}</span>
          <span class="v2-liquidity-chip">CE OI Δ ${ceOiDelta >= 0 ? '+' : ''}${ceOiDelta.toFixed(0)}</span>
          <span class="v2-liquidity-chip">PE OI Δ ${peOiDelta >= 0 ? '+' : ''}${peOiDelta.toFixed(0)}</span>
          <span class="v2-liquidity-chip">Net OI Δ ${netOiDelta >= 0 ? '+' : ''}${netOiDelta.toFixed(0)}</span>
          <span class="v2-liquidity-chip">ATM Net Δ ${atmNetDelta >= 0 ? '+' : ''}${atmNetDelta.toFixed(0)}</span>
        </div>
        <div class="v2-options-compare__rows">
          ${strikeShiftRows.map((row) => {
            const tone = row.delta >= 0 ? 'pos' : 'neg';
            return `<div class="v2-options-compare__row"><span>${row.strike}</span><span class="${tone}">${row.delta >= 0 ? '+' : ''}${row.delta.toFixed(0)}</span></div>`;
          }).join('')}
        </div>
      `;
    };

    const draw = (snapshot: UpstoxOptionsPayload, recordTrend: boolean = false) => {
      const policy = getOptionsLiquidityPolicy(snapshot.symbol || symbol);

        const sample = measureFrame('options-microstructure', () => {
            const oiImbalance = snapshot.strikes.map(s => s.peOI - s.ceOI);
            renderLineChart(lineCanvas, oiImbalance, '#5aa6ff');

            renderDonutChart(
                donutCanvas,
                [snapshot.totalPeOI || 0, snapshot.totalCeOI || 0],
                ['#15b57a', '#d94736'],
            );

            const cols = 12;
            const rows = 3;
            const cells = snapshot.strikes.slice(0, cols * rows).map((strike, idx) => {
                const ceSpread = strike.ceBidAsk ? Math.max(0, strike.ceBidAsk.ask - strike.ceBidAsk.bid) : 0;
                const peSpread = strike.peBidAsk ? Math.max(0, strike.peBidAsk.ask - strike.peBidAsk.bid) : 0;
                const spreadScore = (ceSpread + peSpread) * -1;
                return {
                    x: idx % cols,
                    y: Math.floor(idx / cols),
                    value: spreadScore,
                };
            });
            renderHeatmapGrid(heatmapCanvas, cells, cols, rows);
        });

          const evaluated = snapshot.strikes.slice(0, 30);
          const spreadPairs: number[] = [];
          const spreadPctPairs: number[] = [];
          const depthValues: number[] = [];

          evaluated.forEach((strike) => {
            const appendPair = (bidAsk?: { bid: number; ask: number } | null, volume?: number) => {
              if (!bidAsk) return;
              const spread = Math.max(0, bidAsk.ask - bidAsk.bid);
              const mid = (bidAsk.ask + bidAsk.bid) / 2;
              spreadPairs.push(spread);
              if (mid > 0) {
                spreadPctPairs.push((spread / mid) * 100);
              }
              depthValues.push(volume || 0);
            };

            appendPair(strike.ceBidAsk, strike.ceVolume);
            appendPair(strike.peBidAsk, strike.peVolume);
          });

          const averageSpread = spreadPairs.length
            ? spreadPairs.reduce((sum, v) => sum + v, 0) / spreadPairs.length
            : 0;
          const averageSpreadPct = spreadPctPairs.length
            ? spreadPctPairs.reduce((sum, v) => sum + v, 0) / spreadPctPairs.length
            : 0;
          const averageDepth = depthValues.length
            ? depthValues.reduce((sum, v) => sum + v, 0) / depthValues.length
            : 0;

          const wideSpread = averageSpread >= policy.warningAvgSpread;
          const thinDepth = averageDepth > 0 && averageDepth < policy.warningAvgDepth;
          const slippageRisk = averageSpreadPct >= policy.warningAvgSpreadPct;

          const warnings: string[] = [];
          if (wideSpread) warnings.push('Wide spread warning');
          if (thinDepth) warnings.push('Thin depth warning');
          if (slippageRisk) warnings.push('Slippage anomaly warning');

          warningsNode.innerHTML = warnings.length > 0
            ? `
              <span class="v2-liquidity-chip v2-liquidity-chip--warn">${warnings.join(' | ')}</span>
              <span class="v2-liquidity-chip">avg spread ${averageSpread.toFixed(2)}</span>
              <span class="v2-liquidity-chip">avg spread % ${averageSpreadPct.toFixed(2)}%</span>
              <span class="v2-liquidity-chip">avg depth ${averageDepth.toFixed(0)}</span>
              <span class="v2-liquidity-chip">policy ${policy.symbol}</span>
            `
            : `
              <span class="v2-liquidity-chip v2-liquidity-chip--ok">Liquidity within thresholds</span>
              <span class="v2-liquidity-chip">avg spread ${averageSpread.toFixed(2)}</span>
              <span class="v2-liquidity-chip">avg spread % ${averageSpreadPct.toFixed(2)}%</span>
              <span class="v2-liquidity-chip">avg depth ${averageDepth.toFixed(0)}</span>
              <span class="v2-liquidity-chip">policy ${policy.symbol}</span>
            `;

        const anomalyRows = evaluated.map((strike) => {
            const ceSpread = strike.ceBidAsk ? Math.max(0, strike.ceBidAsk.ask - strike.ceBidAsk.bid) : 0;
            const peSpread = strike.peBidAsk ? Math.max(0, strike.peBidAsk.ask - strike.peBidAsk.bid) : 0;
            const spread = ceSpread + peSpread;

            const ceMid = strike.ceBidAsk ? (strike.ceBidAsk.ask + strike.ceBidAsk.bid) / 2 : 0;
            const peMid = strike.peBidAsk ? (strike.peBidAsk.ask + strike.peBidAsk.bid) / 2 : 0;
            const ceSpreadPct = ceMid > 0 ? (ceSpread / ceMid) * 100 : 0;
            const peSpreadPct = peMid > 0 ? (peSpread / peMid) * 100 : 0;
            const spreadPct = ceSpreadPct + peSpreadPct;

            const depth = (strike.ceVolume || 0) + (strike.peVolume || 0);
            const depthRisk = depth > 0 ? Math.max(0, policy.warningAvgDepth - depth) / policy.warningAvgDepth : 1;
            const spreadRisk = Math.min(1, spreadPct / policy.overlayMaxSpreadPct);
            const score = Math.min(1, (spreadRisk * policy.overlaySpreadWeight) + (depthRisk * policy.overlayDepthWeight));

            return {
                strike: strike.strike,
                spread,
                spreadPct,
                depth,
                score,
            };
        }).sort((a, b) => b.score - a.score).slice(0, 6);

        overlayNode.innerHTML = anomalyRows.length
            ? `
              <div class="v2-liquidity-overlay__title">Liquidity Anomaly Overlay</div>
              <div class="v2-liquidity-overlay__rows">
                ${anomalyRows.map((row) => {
                    const level = row.score >= policy.criticalScore ? 'critical' : row.score >= policy.elevatedScore ? 'elevated' : 'normal';
                    return `
                      <div class="v2-liquidity-overlay__row v2-liquidity-overlay__row--${level}">
                        <span class="v2-liquidity-overlay__strike">${row.strike}</span>
                        <div class="v2-liquidity-overlay__bar-track"><div class="v2-liquidity-overlay__bar" style="width:${(row.score * 100).toFixed(1)}%"></div></div>
                        <span class="v2-liquidity-overlay__meta">spr ${row.spread.toFixed(2)} | ${row.spreadPct.toFixed(2)}% | dep ${row.depth.toFixed(0)}</span>
                      </div>
                    `;
                }).join('')}
              </div>
            `
            : '<div class="v2-liquidity-overlay__empty">No anomaly rows in current chain window.</div>';

        const sortedByStrike = [...snapshot.strikes].sort((a, b) => a.strike - b.strike);
        const atmIndex = sortedByStrike.reduce((bestIdx, row, idx, arr) => {
          if (bestIdx < 0) return idx;
          const currentDistance = Math.abs(row.strike - snapshot.spotPrice);
          const bestDistance = Math.abs(arr[bestIdx].strike - snapshot.spotPrice);
          return currentDistance < bestDistance ? idx : bestIdx;
        }, -1);
        const ladderStart = Math.max(0, atmIndex - 3);
        const ladderEnd = Math.min(sortedByStrike.length, ladderStart + 7);
        const ladderRows = sortedByStrike.slice(ladderStart, ladderEnd).map((row) => {
          const netChange = row.peChange - row.ceChange;
          return {
            strike: row.strike,
            ceChange: row.ceChange,
            peChange: row.peChange,
            netChange,
          };
        });

        ladderNode.innerHTML = ladderRows.length
          ? `
            <div class="v2-options-ladder__head">
              <span>Strike</span>
              <span>CE OI Δ</span>
              <span>PE OI Δ</span>
              <span>Net (PE-CE)</span>
            </div>
            ${ladderRows.map((row) => {
              const ceClass = row.ceChange >= 0 ? 'pos' : 'neg';
              const peClass = row.peChange >= 0 ? 'pos' : 'neg';
              const netClass = row.netChange >= 0 ? 'pos' : 'neg';
              return `
                <div class="v2-options-ladder__row">
                  <span>${row.strike}</span>
                  <span class="${ceClass}">${row.ceChange >= 0 ? '+' : ''}${row.ceChange.toFixed(0)}</span>
                  <span class="${peClass}">${row.peChange >= 0 ? '+' : ''}${row.peChange.toFixed(0)}</span>
                  <span class="${netClass}">${row.netChange >= 0 ? '+' : ''}${row.netChange.toFixed(0)}</span>
                </div>
              `;
            }).join('')}
          `
          : '<div class="v2-options-ladder__empty">No ladder rows available.</div>';

        const flowRows = snapshot.strikes.map((row) => {
          const ceFlow = row.ceOI > 0 ? (row.ceVolume || 0) / row.ceOI : 0;
          const peFlow = row.peOI > 0 ? (row.peVolume || 0) / row.peOI : 0;
          const flowScore = ceFlow + peFlow;
          const combinedVolume = (row.ceVolume || 0) + (row.peVolume || 0);
          return {
            strike: row.strike,
            ceFlow,
            peFlow,
            flowScore,
            combinedVolume,
          };
        }).sort((a, b) => b.flowScore - a.flowScore).slice(0, 6);

        unusualFlowNode.innerHTML = flowRows.length
          ? `
            <div class="v2-options-flow__head">
              <span>Strike</span>
              <span>CE Vol/OI</span>
              <span>PE Vol/OI</span>
              <span>Flow Score</span>
            </div>
            ${flowRows.map((row) => `
              <div class="v2-options-flow__row">
                <span>${row.strike}</span>
                <span>${row.ceFlow.toFixed(3)}</span>
                <span>${row.peFlow.toFixed(3)}</span>
                <span>${row.flowScore.toFixed(3)} | vol ${row.combinedVolume.toFixed(0)}</span>
              </div>
            `).join('')}
          `
          : '<div class="v2-options-flow__empty">No unusual flow rows available.</div>';

        const flowUniverse = snapshot.strikes.map((row) => {
          const ceFlow = row.ceOI > 0 ? (row.ceVolume || 0) / row.ceOI : 0;
          const peFlow = row.peOI > 0 ? (row.peVolume || 0) / row.peOI : 0;
          return {
            strike: row.strike,
            score: ceFlow + peFlow,
            skew: peFlow - ceFlow,
          };
        }).sort((a, b) => b.score - a.score);

        const totalFlowScore = flowUniverse.reduce((sum, row) => sum + row.score, 0);
        const topThreeFlow = flowUniverse.slice(0, 3).reduce((sum, row) => sum + row.score, 0);
        const concentrationPct = totalFlowScore > 0 ? (topThreeFlow / totalFlowScore) * 100 : 0;
        const leading = flowUniverse[0] || null;
        const aggregateSkew = flowUniverse.reduce((sum, row) => sum + row.skew, 0);

        flowConcentrationNode.innerHTML = `
          <div class="terminal-list__row"><span>Top-3 Flow Share</span><strong>${concentrationPct.toFixed(1)}%</strong></div>
          <div class="terminal-list__row"><span>Lead Strike</span><strong>${leading ? `${leading.strike} (${leading.score.toFixed(3)})` : 'n/a'}</strong></div>
          <div class="terminal-list__row"><span>Flow Skew (PE-CE)</span><strong class="${aggregateSkew >= 0 ? 'terminal-text--up' : 'terminal-text--down'}">${aggregateSkew >= 0 ? '+' : ''}${aggregateSkew.toFixed(3)}</strong></div>
          <div class="terminal-list__row"><span>Total Flow Score</span><strong>${totalFlowScore.toFixed(3)}</strong></div>
        `;

        const atmBandRows = sortedByStrike.slice(Math.max(0, atmIndex - 1), Math.min(sortedByStrike.length, atmIndex + 2));
        const atmCenter = sortedByStrike[atmIndex] || null;
        const atmNetOi = atmCenter ? (atmCenter.peOI - atmCenter.ceOI) : 0;
        const atmNetChange = atmCenter ? (atmCenter.peChange - atmCenter.ceChange) : 0;
        const atmBandNetOi = atmBandRows.reduce((sum, row) => sum + (row.peOI - row.ceOI), 0);
        const atmPressure = atmBandRows.length > 0 ? atmBandNetOi / atmBandRows.length : 0;
        const atmPressureTone = atmPressure >= 0 ? 'terminal-text--up' : 'terminal-text--down';
        const atmChangeTone = atmNetChange >= 0 ? 'terminal-text--up' : 'terminal-text--down';

        atmPressureNode.innerHTML = `
          <div class="terminal-list__row"><span>ATM Strike</span><strong>${atmCenter ? atmCenter.strike : 'n/a'}</strong></div>
          <div class="terminal-list__row"><span>ATM Net OI (PE-CE)</span><strong class="${atmNetOi >= 0 ? 'terminal-text--up' : 'terminal-text--down'}">${atmNetOi >= 0 ? '+' : ''}${atmNetOi.toFixed(0)}</strong></div>
          <div class="terminal-list__row"><span>ATM Net OI Delta</span><strong class="${atmChangeTone}">${atmNetChange >= 0 ? '+' : ''}${atmNetChange.toFixed(0)}</strong></div>
          <div class="terminal-list__row"><span>ATM Band Pressure</span><strong class="${atmPressureTone}">${atmPressure >= 0 ? '+' : ''}${atmPressure.toFixed(0)}</strong></div>
        `;

        const depthWindowStart = Math.max(0, atmIndex - 5);
        const depthWindowEnd = Math.min(sortedByStrike.length, atmIndex + 7);
        const depthRows = sortedByStrike.slice(depthWindowStart, depthWindowEnd).map((row) => {
          const ceDepth = row.ceVolume || 0;
          const peDepth = row.peVolume || 0;
          const totalDepth = ceDepth + peDepth;
          const imbalancePct = totalDepth > 0 ? ((peDepth - ceDepth) / totalDepth) * 100 : 0;
          const bias = imbalancePct > 12 ? 'pe' : imbalancePct < -12 ? 'ce' : 'neutral';
          const intensity = Math.min(100, Math.abs(imbalancePct) * 1.8);
          return {
            strike: row.strike,
            ceDepth,
            peDepth,
            imbalancePct,
            bias,
            intensity,
          };
        });

        depthStripNode.innerHTML = depthRows.length
          ? `
            <div class="v2-options-depth-strip__meta">Expiry ${snapshot.expiry} | Spot ${snapshot.spotPrice.toFixed(2)}</div>
            <div class="v2-options-depth-strip__rows">
              ${depthRows.map((row) => `
                <div class="v2-options-depth-strip__row">
                  <span class="v2-options-depth-strip__strike">${row.strike}</span>
                  <span class="v2-options-depth-strip__value">CE ${row.ceDepth.toFixed(0)}</span>
                  <div class="v2-options-depth-strip__bar-track">
                    <div class="v2-options-depth-strip__bar v2-options-depth-strip__bar--${row.bias}" style="width:${row.intensity.toFixed(1)}%"></div>
                  </div>
                  <span class="v2-options-depth-strip__value">PE ${row.peDepth.toFixed(0)}</span>
                  <span class="v2-options-depth-strip__imbalance ${row.bias === 'pe' ? 'pos' : row.bias === 'ce' ? 'neg' : ''}">${row.imbalancePct >= 0 ? '+' : ''}${row.imbalancePct.toFixed(1)}%</span>
                </div>
              `).join('')}
            </div>
          `
          : '<div class="v2-options-depth-strip__empty">No depth rows available in ATM window.</div>';

        type StressBucket = {
          id: string;
          label: string;
          rows: Array<{ spreadPct: number; depth: number }>;
        };

        const stressBuckets: StressBucket[] = [
          { id: 'put', label: 'PUT WING', rows: [] },
          { id: 'atm', label: 'ATM BAND', rows: [] },
          { id: 'call', label: 'CALL WING', rows: [] },
        ];

        evaluated.forEach((row) => {
          const ceSpread = row.ceBidAsk ? Math.max(0, row.ceBidAsk.ask - row.ceBidAsk.bid) : 0;
          const peSpread = row.peBidAsk ? Math.max(0, row.peBidAsk.ask - row.peBidAsk.bid) : 0;
          const ceMid = row.ceBidAsk ? (row.ceBidAsk.ask + row.ceBidAsk.bid) / 2 : 0;
          const peMid = row.peBidAsk ? (row.peBidAsk.ask + row.peBidAsk.bid) / 2 : 0;
          const ceSpreadPct = ceMid > 0 ? (ceSpread / ceMid) * 100 : 0;
          const peSpreadPct = peMid > 0 ? (peSpread / peMid) * 100 : 0;
          const spreadPct = ceSpreadPct + peSpreadPct;
          const depth = (row.ceVolume || 0) + (row.peVolume || 0);

          const distancePct = snapshot.spotPrice > 0
            ? (Math.abs(row.strike - snapshot.spotPrice) / snapshot.spotPrice) * 100
            : 0;

          if (distancePct <= 0.6) {
            stressBuckets[1].rows.push({ spreadPct, depth });
            return;
          }
          if (row.strike < snapshot.spotPrice) {
            stressBuckets[0].rows.push({ spreadPct, depth });
            return;
          }
          stressBuckets[2].rows.push({ spreadPct, depth });
        });

        const clusterRows = stressBuckets.map((bucket) => {
          const count = bucket.rows.length;
          const avgSpreadPct = count > 0
            ? bucket.rows.reduce((sum, row) => sum + row.spreadPct, 0) / count
            : 0;
          const avgDepth = count > 0
            ? bucket.rows.reduce((sum, row) => sum + row.depth, 0) / count
            : 0;

          const spreadRisk = Math.min(1, avgSpreadPct / policy.overlayMaxSpreadPct);
          const depthRisk = avgDepth > 0 ? Math.max(0, policy.warningAvgDepth - avgDepth) / policy.warningAvgDepth : 1;
          const score = Math.min(1, (spreadRisk * policy.overlaySpreadWeight) + (depthRisk * policy.overlayDepthWeight));
          const level: ClusterLevel = score >= policy.criticalScore ? 'critical' : score >= policy.elevatedScore ? 'elevated' : 'normal';

          return {
            id: bucket.id as ClusterId,
            label: bucket.label,
            count,
            avgSpreadPct,
            avgDepth,
            score,
            level,
          };
        });

        if (recordTrend) {
          putClusterTrend.push(clusterRows[0]?.score || 0);
          atmClusterTrend.push(clusterRows[1]?.score || 0);
          callClusterTrend.push(clusterRows[2]?.score || 0);
        }

        const now = Date.now();
        clusterRows.forEach((cluster) => {
          const state = clusterAlerts[cluster.id];
          if (state.level === cluster.level) {
            state.pendingLevel = null;
            state.pendingCount = 0;
          } else {
            const currentRank = levelRank(state.level);
            const targetRank = levelRank(cluster.level);
            const requiredWindows = targetRank > currentRank
              ? policy.clusterHysteresisUpWindows
              : policy.clusterHysteresisDownWindows;

            if (state.pendingLevel === cluster.level) {
              state.pendingCount += 1;
            } else {
              state.pendingLevel = cluster.level;
              state.pendingCount = 1;
            }

            if (state.pendingCount >= requiredWindows) {
              state.level = cluster.level;
              state.enteredAt = now;
              state.pendingLevel = null;
              state.pendingCount = 0;
            }
          }

          state.score = cluster.score;
          state.label = cluster.label;
        });

        const orderedStates = Object.values(clusterAlerts).sort((a, b) => {
          const rankDiff = levelRank(b.level) - levelRank(a.level);
          if (rankDiff !== 0) return rankDiff;
          return b.score - a.score;
        });
        const topState = orderedStates[0];
        const topDwellMs = Math.max(0, now - topState.enteredAt);

        document.dispatchEvent(new CustomEvent('imm:options:cluster-alert', {
          detail: {
            symbol,
            expiry: snapshot.expiry,
            level: topState.level,
            cluster: topState.label,
            dwellMs: topDwellMs,
            score: topState.score,
            summary: `${topState.label} ${topState.level.toUpperCase()} ${topState.score.toFixed(2)} (${formatDwell(topDwellMs)})`,
          },
        }));

        const activeAlerts = Object.values(clusterAlerts)
          .filter((item) => item.level !== 'normal')
          .sort((a, b) => levelRank(b.level) - levelRank(a.level));

        if (activeAlerts.length > 0) {
          warningsNode.innerHTML += activeAlerts.map((item) => {
            const dwellMs = Math.max(0, now - item.enteredAt);
            const chipClass = item.level === 'critical' ? 'v2-liquidity-chip--warn' : '';
            return `<span class="v2-liquidity-chip ${chipClass}">${item.label} ${item.level.toUpperCase()} | ${item.score.toFixed(2)} | ${formatDwell(dwellMs)}</span>`;
          }).join('');
        }

        clusterNode.innerHTML = `
          <div class="v2-options-cluster__meta">Expiry ${snapshot.expiry} | Policy ${policy.symbol}</div>
          <div class="v2-options-cluster__rows">
            ${clusterRows.map((cluster) => `
              <div class="v2-options-cluster__row v2-options-cluster__row--${cluster.level}">
                <span class="v2-options-cluster__label">${cluster.label}</span>
                <div class="v2-options-cluster__bar-track"><div class="v2-options-cluster__bar" style="width:${(cluster.score * 100).toFixed(1)}%"></div></div>
                <span class="v2-options-cluster__meta-value">spr ${cluster.avgSpreadPct.toFixed(2)}% | dep ${cluster.avgDepth.toFixed(0)} | n ${cluster.count}</span>
              </div>
            `).join('')}
          </div>
        `;

        renderLineChart(putTrendCanvas, putClusterTrend.toArray(), '#15b57a');
        renderLineChart(atmTrendCanvas, atmClusterTrend.toArray(), '#5aa6ff');
        renderLineChart(callTrendCanvas, callClusterTrend.toArray(), '#d94736');
        renderLineChart(pcrPulseCanvas, pcrSeries.toArray(), '#ff9f43');
        renderLineChart(oiSkewPulseCanvas, oiSkewSeries.toArray(), '#7c8cff');
        renderLineChart(flowImbalancePulseCanvas, flowImbalanceSeries.toArray(), '#5aa6ff');
        renderLineChart(atmVolumePulseCanvas, atmVolumePulseSeries.toArray(), '#15b57a');
        renderLineChart(oiConcentrationPulseCanvas, oiConcentrationSeries.toArray(), '#f5c542');

        compareNode.innerHTML = buildCrossExpiryRows(snapshot, compareSnapshot);

        frameTracker.push(sample.label, sample.durationMs);
        const avg = frameTracker.getAverageMs();
        const within = frameTracker.isWithinBudget(sample.durationMs);
        frameNode.textContent = `Frame: ${sample.durationMs.toFixed(2)}ms | avg ${avg.toFixed(2)}ms | budget ${within ? 'OK' : 'OVER'}`;
        reportChartRenderMetric({
          id: 'phase2-options-microstructure',
          label: 'Options Microstructure',
          latestMs: sample.durationMs,
          averageMs: avg,
          budgetMs: frameTracker.getBudgetMs(),
          withinBudget: within,
        });

        const stale = computeStaleState(lastUpdatedAt, staleThresholdMs);
        staleNode.style.display = stale.stale ? 'flex' : 'none';
        freshnessNode.textContent = stale.stale
            ? `Stale: ${formatAge(stale.ageMs)} old`
            : `Live update ${new Date(lastUpdatedAt || Date.now()).toLocaleTimeString('en-IN')}`;

        contextNode.textContent = `${snapshot.symbol} | PCR ${snapshot.pcr.toFixed(2)} | Spot ${snapshot.spotPrice.toFixed(2)} | Strikes ${snapshot.strikes.length} | Hys U/D ${policy.clusterHysteresisUpWindows}/${policy.clusterHysteresisDownWindows}`;
    };

    const setActiveSymbolButton = () => {
      container.querySelectorAll('.v2-opt-btn').forEach((node) => {
        const current = node.getAttribute('data-opt-symbol')?.toUpperCase();
        node.classList.toggle('glass-btn--primary', current === symbol);
      });
    };

    const update = async () => {
        try {
            const result = await apiFetch<UpstoxOptionsPayload>('/upstox-options', {
                cacheTTL: 10,
                staleWhileRevalidate: false,
                params: {
                  symbol,
                  ...(primaryExpiry ? { expiry: primaryExpiry } : {}),
                },
            });
            if (!result.data?.strikes?.length) throw new Error('no option data');

            primaryExpiry = result.data.expiry;
            syncExpiryControls(result.data.expiry);

            let compareData: UpstoxOptionsPayload | null = null;
            if (compareExpiry && compareExpiry !== result.data.expiry) {
              try {
                const compareResult = await apiFetch<UpstoxOptionsPayload>('/upstox-options', {
                  cacheTTL: 10,
                  staleWhileRevalidate: false,
                  params: {
                    symbol,
                    expiry: compareExpiry,
                  },
                });
                if (compareResult.data?.strikes?.length) {
                  compareData = compareResult.data;
                }
              } catch {
                compareData = compareSnapshot;
              }
            }

            compareSnapshot = compareData;
            lastSnapshot = result.data;
            lastUpdatedAt = Date.now();
            pcrSeries.push(result.data.pcr || 0);
            const totalOi = (result.data.totalCeOI || 0) + (result.data.totalPeOI || 0);
            const oiSkew = totalOi > 0 ? ((result.data.totalPeOI - result.data.totalCeOI) / totalOi) * 100 : 0;
            oiSkewSeries.push(oiSkew);

            const totalCeVolume = result.data.strikes.reduce((sum, strike) => sum + (strike.ceVolume || 0), 0);
            const totalPeVolume = result.data.strikes.reduce((sum, strike) => sum + (strike.peVolume || 0), 0);
            const totalVolume = totalCeVolume + totalPeVolume;
            const flowImbalance = totalVolume > 0 ? ((totalPeVolume - totalCeVolume) / totalVolume) * 100 : 0;
            flowImbalanceSeries.push(flowImbalance);

            const sortedStrikes = [...result.data.strikes].sort((a, b) => a.strike - b.strike);
            const atmIdx = sortedStrikes.reduce((bestIdx, row, idx, arr) => {
              if (bestIdx < 0) return idx;
              const distance = Math.abs(row.strike - result.data.spotPrice);
              const bestDistance = Math.abs(arr[bestIdx].strike - result.data.spotPrice);
              return distance < bestDistance ? idx : bestIdx;
            }, -1);
            const atmWindow = sortedStrikes.slice(Math.max(0, atmIdx - 1), Math.min(sortedStrikes.length, atmIdx + 2));
            const atmVolume = atmWindow.length > 0
              ? atmWindow.reduce((sum, row) => sum + (row.ceVolume || 0) + (row.peVolume || 0), 0) / atmWindow.length
              : 0;
            atmVolumePulseSeries.push(atmVolume);

            const strikeOi = result.data.strikes.map((strike) => (strike.ceOI || 0) + (strike.peOI || 0));
            const totalStrikeOi = strikeOi.reduce((sum, value) => sum + value, 0);
            const topOi = [...strikeOi].sort((a, b) => b - a).slice(0, 5).reduce((sum, value) => sum + value, 0);
            const oiConcentration = totalStrikeOi > 0 ? (topOi / totalStrikeOi) * 100 : 0;
            oiConcentrationSeries.push(oiConcentration);

            setSnapshot<OptionsPersistedSnapshot>(snapshotKey, {
              symbol,
              primaryExpiry,
              compareExpiry,
              payload: result.data,
              comparePayload: compareSnapshot,
              pcrSeries: pcrSeries.toArray(),
              oiSkewSeries: oiSkewSeries.toArray(),
              flowImbalanceSeries: flowImbalanceSeries.toArray(),
              atmVolumePulseSeries: atmVolumePulseSeries.toArray(),
              oiConcentrationSeries: oiConcentrationSeries.toArray(),
              clusterTrends: {
                put: putClusterTrend.toArray(),
                atm: atmClusterTrend.toArray(),
                call: callClusterTrend.toArray(),
              },
              lastUpdatedAt: lastUpdatedAt,
            });
            draw(result.data, true);
        } catch {
            if (lastSnapshot) {
                draw(lastSnapshot);
            }
        }
    };

    container.querySelectorAll('.v2-opt-btn').forEach(node => {
        node.addEventListener('click', () => {
            const selected = node.getAttribute('data-opt-symbol');
            if (!selected) return;
            symbol = selected.toUpperCase();
        primaryExpiry = null;
        compareExpiry = null;
        setActiveSymbolButton();
            void update();
        });
    });

    expirySelect.addEventListener('change', () => {
      primaryExpiry = expirySelect.value || null;
      if (compareExpiry === primaryExpiry) {
        const candidate = Array.from(compareExpirySelect.options).map((option) => option.value).find((value) => value !== primaryExpiry);
        compareExpiry = candidate || compareExpiry;
      }
      if (compareExpiry) {
        compareExpirySelect.value = compareExpiry;
      }
      void update();
    });

    compareExpirySelect.addEventListener('change', () => {
      compareExpiry = compareExpirySelect.value || null;
      void update();
    });

    const onExternalSymbol = (event: Event) => {
      const detail = (event as CustomEvent<SymbolEventDetail>).detail;
      const candidate = detail?.symbol?.toUpperCase();
      if (!candidate) return;
      if (!['NIFTY', 'BANKNIFTY', 'FINNIFTY'].includes(candidate)) return;
      symbol = candidate;
      primaryExpiry = null;
      compareExpiry = null;
      setActiveSymbolButton();
      void update();
    };

    document.addEventListener('imm:options:symbol', onExternalSymbol as EventListener);
    setActiveSymbolButton();
    if (lastSnapshot) {
      primaryExpiry = lastSnapshot.expiry;
      syncExpiryControls(lastSnapshot.expiry);
      draw(lastSnapshot);
    }

    const taskId = `phase2-options-${Math.random().toString(36).slice(2)}`;
    const unregister = chartRefreshScheduler.register(taskId, 'B', update, 3000);

    teardownMap.set(container, () => {
      document.removeEventListener('imm:options:symbol', onExternalSymbol as EventListener);
        unregister();
    });
}
